import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Building2,
  Search,
  Loader2,
  CheckCircle2,
  XCircle,
  FileText,
  Calendar,
  FolderOpen,
} from "lucide-react";

import adminService from "@/services/adminService";
import SubmissionFilesDrawer from "@/components/admin/SubmissionFilesDrawer";

const STATUS_COLORS = {
  DRAFT: "bg-slate-500/20 text-slate-300",
  SUBMITTED: "bg-yellow-500/20 text-yellow-300",
  UNDER_REVIEW: "bg-blue-500/20 text-blue-300",
  APPROVED: "bg-green-500/20 text-green-300",
  REJECTED: "bg-red-500/20 text-red-300",
};

export default function AdminPublishers() {
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [reason, setReason] = useState("");
  const [processing, setProcessing] = useState(false);
  const [showFilesDrawer, setShowFilesDrawer] = useState(false);
  const [filesSubmission, setFilesSubmission] = useState(null);

  useEffect(() => {
    loadSubmissions();
  }, []);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      const data = await adminService.getSubmissions();
      setSubmissions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error loading submissions:", error);
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (submission) => {
    try {
      setProcessing(true);
      await adminService.approveSubmission(submission.id);
      toast.success("Submission approved");
      await loadSubmissions();
    } catch (error) {
      console.error("Error approving:", error);
      toast.error("Failed to approve");
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!reason || reason.length < 5) {
      toast.error("Reason required (min 5 characters)");
      return;
    }

    try {
      setProcessing(true);
      await adminService.rejectSubmission(selectedSubmission.id, reason);
      toast.success("Submission rejected");
      setShowRejectModal(false);
      setReason("");
      await loadSubmissions();
    } catch (error) {
      console.error("Error rejecting:", error);
      toast.error("Failed to reject");
    } finally {
      setProcessing(false);
    }
  };

  const filteredSubmissions = submissions.filter(
    (s) =>
      s.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.author_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-publishers">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Publisher Submissions</h1>
          <p className="text-slate-400">Review and approve book submissions</p>
        </div>
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search..."
            className="pl-10 bg-slate-800 border-slate-700 text-white"
          />
        </div>
      </div>

      {/* Submissions List */}
      <div className="space-y-4">
        {filteredSubmissions.map((submission) => (
          <Card key={submission.id} className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-white text-lg">
                      {submission.title}
                    </h3>
                    <Badge className={STATUS_COLORS[submission.submission_status]}>
                      {submission.submission_status}
                    </Badge>
                  </div>
                  {(submission.author_name || submission.owner_user_id) && (
                    <p className="text-slate-400 text-sm mb-2">
                      {submission.author_name
                        ? `by ${submission.author_name}`
                        : `Owner: ${submission.owner_user_id}`}
                    </p>
                  )}
                  <div className="flex items-center gap-4 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <FileText className="w-3 h-3" />
                      {submission.language}
                    </span>
                    <span>{submission.genre}</span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(submission.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  {submission.summary && (
                    <p className="mt-3 text-sm text-slate-400 line-clamp-2">
                      {submission.summary}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-2 ml-4">
                  {/* View Files Button - Always shown */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setFilesSubmission(submission);
                      setShowFilesDrawer(true);
                    }}
                    className="border-purple-500/50 text-purple-400 hover:bg-purple-500/20"
                    data-testid="view-files-btn"
                  >
                    <FolderOpen className="w-4 h-4 mr-1" />
                    View Files
                  </Button>

                  {/* Approve/Reject for pending submissions */}
                  {(submission.submission_status === "SUBMITTED" ||
                    submission.submission_status === "UNDER_REVIEW") && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => handleApprove(submission)}
                        disabled={processing}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedSubmission(submission);
                          setShowRejectModal(true);
                        }}
                        className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredSubmissions.length === 0 && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="py-12 text-center text-slate-500">
            <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No submissions found</p>
          </CardContent>
        </Card>
      )}

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Reject Submission</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-slate-400">
              Rejecting: <span className="text-white">{selectedSubmission?.title}</span>
            </p>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason for rejection (required)..."
              className="bg-slate-700 border-slate-600"
              rows={4}
            />
            <div className="flex gap-3">
              <Button
                onClick={handleReject}
                disabled={processing || reason.length < 5}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                {processing && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Reject
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowRejectModal(false)}
                className="flex-1 border-slate-600"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Submission Files Drawer */}
      <SubmissionFilesDrawer
        isOpen={showFilesDrawer}
        onClose={() => {
          setShowFilesDrawer(false);
          setFilesSubmission(null);
        }}
        submission={filesSubmission}
        onVerifySuccess={() => {
          // Could refresh submissions if needed
        }}
      />
    </div>
  );
}
